import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { User } from '../types';
import { authService, LoginRequest } from '../services/authService';
import { CurrentSubscription } from '../services/subscriptionService';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  lastActivity: Date | null;
  subscription: CurrentSubscription | null; // Aktif abonelik bilgisi
  lastSubscriptionFetch: Date | null; // Subscription son yüklenme zamanı
  subscriptionFetchPromise: Promise<void> | null; // Non-persisted

  // Actions
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  loadSubscription: (businessId: string) => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  setSubscription: (subscription: CurrentSubscription | null) => void;
  updateLastActivity: () => void;
  reset: () => void;

  // Computed
  isTokenExpired: () => boolean;
  shouldRefreshToken: () => boolean;
  hasActiveSubscription: () => boolean; // Aktif abonelik var mı?
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
  lastActivity: null,
  subscription: null,
  lastSubscriptionFetch: null,
  subscriptionFetchPromise: null,
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,

        login: async (credentials: LoginRequest) => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            const response = await authService.login(credentials);

            if (response.success && response.data) {
              // Backend returns flat response: { userId, token, refreshToken, name, email, isGoogleUser }
              const data = response.data as any;
              const token = data.token || data.Token;
              const refreshToken = data.refreshToken || data.RefreshToken || token;
              const expiresIn = data.expiresIn || data.ExpiresIn || 3600;

              // Construct user object from flat response
              const user = data.user || {
                id: data.userId || data.UserId,
                email: data.email || data.Email,
                name: data.name || data.Name,
                surname: data.surname || data.Surname || '',
                role: 'business_owner' as const,
                termsAccepted: data.termsAccepted ?? data.TermsAccepted ?? false,
                termsVersion: data.termsVersion || data.TermsVersion || '',
                termsAcceptedAt: data.termsAcceptedAt || data.TermsAcceptedAt || null,
                createdAt: data.createdAt || data.CreatedAt || new Date().toISOString(),
                updatedAt: data.updatedAt || data.UpdatedAt || new Date().toISOString(),
              };

              // Set auth data in service
              authService.setAuthData(user, token, refreshToken, expiresIn);

              set((state) => {
                state.user = user;
                state.isAuthenticated = true;
                state.isLoading = false;
                state.error = null;
                state.lastActivity = new Date();
              });

              // Login sonrası duyuruları göster
              sessionStorage.setItem('show_announcements', 'true');

              // Hemen ardından /auth/me'den güncel user verisini çek (termsAccepted, termsVersion vb.)
              try {
                await get().refreshUser();
              } catch (e) {
                console.warn('[authStore] refreshUser after login failed:', e);
              }

              // Login başarılı olduktan sonra business, subscription ve overview bilgisini yükle
              try {
                const { useDashboardStore } = await import('./dashboardStore');
                const dashboardStore = useDashboardStore.getState();

                // Önce business bilgilerini yükle
                await dashboardStore.fetchBusinesses();

                // Business ID'yi al
                const businessId = dashboardStore.businessId || await (await import('../utils/menuHelper')).getDefaultBusinessId();

                if (businessId) {
                  await Promise.all([
                    get().loadSubscription(businessId),
                    // Overview'ı login sırasında bir kez yükle
                    dashboardStore.fetchOverview(businessId).catch(error => {
                      console.warn('Failed to load overview on login:', error);
                    })
                  ]);
                }
              } catch (error) {
                console.warn('Failed to load data on login:', error);
              }

              return true;
            } else {
              set((state) => {
                state.error = response.message || 'Giriş başarısız';
                state.isLoading = false;
              });
              return false;
            }
          } catch (error) {
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Bilinmeyen hata';
              state.isLoading = false;
            });
            return false;
          }
        },

        logout: async () => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            await authService.logout();
          } catch (error) {
            console.warn('Logout error:', error);
          } finally {
            // State'i temizle
            set((state) => {
              state.user = null;
              state.isAuthenticated = false;
              state.isLoading = false;
              state.error = null;
              state.lastActivity = null;
              state.subscription = null;
            });

            // Zustand persist storage'ı temizle (localStorage'dan)
            try {
              localStorage.removeItem('auth-store');
            } catch (e) {
              console.warn('Failed to clear auth-store from localStorage:', e);
            }

            // Diğer store'ları da temizle
            try {
              const { useDashboardStore } = await import('./dashboardStore');
              useDashboardStore.getState().reset();
              localStorage.removeItem('dashboard-store');
            } catch (e) {
              console.warn('Failed to clear dashboard-store:', e);
            }

            try {
              const { useCategoryStore } = await import('./categoryStore');
              useCategoryStore.getState().reset();
              localStorage.removeItem('category-store');
            } catch (e) {
              console.warn('Failed to clear category-store:', e);
            }

            try {
              const { useProductStore } = await import('./productStore');
              useProductStore.getState().reset();
              localStorage.removeItem('product-store');
            } catch (e) {
              console.warn('Failed to clear product-store:', e);
            }
          }
        },

        loadSubscription: async (businessId: string) => {
          const state = get();

          // 1. In-flight Request Deduplication
          if (state.subscriptionFetchPromise) {
            return state.subscriptionFetchPromise;
          }

          // Cache kontrolü: Eğer subscription var ve son 5 dakikada yüklendiyse, tekrar yükleme
          const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika
          const now = Date.now();
          const lastFetchTime = state.lastSubscriptionFetch ? new Date(state.lastSubscriptionFetch).getTime() : 0;
          const isCacheValid = state.subscription !== null && (now - lastFetchTime) < CACHE_DURATION;

          if (isCacheValid) {
            return;
          }

          const fetchPromise = (async () => {
            try {
              const { subscriptionService } = await import('../services/subscriptionService');
              const response = await subscriptionService.getCurrentSubscription(businessId);

              // İstek iptal edildiyse (sayfa yenilendi vb.) işlem yapma
              if (!response.success && response.message === 'İstek iptal edildi') {
                return;
              }

              if (response.success && response.data) {
                // Backend'den gelen subscriptionType enum (0, 1, 2) veya string olabilir
                // String'e çevir: 0=Free, 1=Basic, 2=Premium
                const subscriptionData = response.data;
                let subscriptionType: 'Free' | 'Basic' | 'Premium' = 'Free';

                if (typeof subscriptionData.subscriptionType === 'number') {
                  // Enum sayı olarak geldi
                  subscriptionType = subscriptionData.subscriptionType === 0 ? 'Free' :
                    subscriptionData.subscriptionType === 1 ? 'Basic' :
                      subscriptionData.subscriptionType === 2 ? 'Premium' : 'Free';
                } else if (typeof subscriptionData.subscriptionType === 'string') {
                  // String olarak geldi - normalize et (case-insensitive)
                  const typeStr = subscriptionData.subscriptionType.trim();
                  if (typeStr.toLowerCase() === 'premium') {
                    subscriptionType = 'Premium';
                  } else if (typeStr.toLowerCase() === 'basic') {
                    subscriptionType = 'Basic';
                  } else if (typeStr.toLowerCase() === 'free') {
                    subscriptionType = 'Free';
                  } else {
                    // Fallback: string'i direkt kullan
                    subscriptionType = subscriptionData.subscriptionType as 'Free' | 'Basic' | 'Premium';
                  }
                }


                set((state) => {
                  state.subscription = {
                    ...subscriptionData,
                    subscriptionType
                  };
                  state.lastSubscriptionFetch = new Date(); // Cache timestamp güncelle
                });
              } else {
                // Abonelik yoksa null set et
                set((state) => {
                  state.subscription = null;
                  state.lastSubscriptionFetch = new Date(); // Cache timestamp güncelle
                });
              }
            } catch (error: any) {
              // AbortError'ı ignore et (sayfa yenilendi, component unmount oldu)
              if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
                return;
              }
              console.warn('Failed to load subscription:', error);
              set((state) => {
                state.subscription = null;
              });
            } finally {
              set((state) => { state.subscriptionFetchPromise = null; });
            }
          })();

          set((state) => { state.subscriptionFetchPromise = fetchPromise; });
          return fetchPromise;
        },

        refreshUser: async () => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            const response = await authService.getCurrentUser();

            if (response.success && response.data) {
              set((state) => {
                state.user = response.data as User;
                state.isAuthenticated = true;
                state.isLoading = false;
                state.error = null;
                state.lastActivity = new Date();
              });
            } else {
              // TERMS_NOT_ACCEPTED durumunda auth state'i koruyalım
              const errorCode = (response.error as any)?.errorCode || (response.error as any)?.ErrorCode || '';
              const isTermsError = errorCode === 'TERMS_NOT_ACCEPTED' ||
                (response.message || '').toLowerCase().includes('terms');

              if (isTermsError) {
                // Kullanıcı authenticated ama terms kabul etmemiş
                // User state'i koruyalım, sadece termsAccepted false olarak kalsın
                set((state) => {
                  state.isLoading = false;
                  state.error = null;
                  if (state.user) {
                    state.user.termsAccepted = false;
                  }
                });
              } else {
                set((state) => {
                  state.user = null;
                  state.isAuthenticated = false;
                  state.isLoading = false;
                  state.error = response.message || 'Kullanıcı bilgileri alınamadı';
                });
              }
            }
          } catch (error) {
            set((state) => {
              state.user = null;
              state.isAuthenticated = false;
              state.isLoading = false;
              state.error = error instanceof Error ? error.message : 'Bilinmeyen hata';
            });
          }
        },

        clearError: () => {
          set((state) => {
            state.error = null;
          });
        },

        setUser: (user: User | null) => {
          set((state) => {
            state.user = user;
            state.isAuthenticated = !!user;
            state.lastActivity = user ? new Date() : null;
            if (!user) {
              state.subscription = null;
            }
          });
        },

        setSubscription: (subscription: CurrentSubscription | null) => {
          set((state) => {
            state.subscription = subscription;
          });
        },

        updateLastActivity: () => {
          set((state) => {
            state.lastActivity = new Date();
          });
        },

        reset: () => {
          set(() => ({ ...initialState }));
        },

        // Computed getters
        isTokenExpired: () => {
          return authService.isTokenExpired();
        },

        shouldRefreshToken: () => {
          return authService.shouldRefreshToken();
        },

        hasActiveSubscription: () => {
          const state = get();
          return state.subscription !== null &&
            state.subscription.isActive &&
            state.subscription.subscriptionType !== 'Free' &&
            new Date(state.subscription.endDate) > new Date();
        },
      })),
      {
        name: 'auth-store',
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          lastActivity: state.lastActivity,
          subscription: state.subscription,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Auto-refresh token when needed
let refreshInterval: NodeJS.Timeout | null = null;

export const startTokenRefresh = () => {
  if (refreshInterval) return;

  refreshInterval = setInterval(async () => {
    const state = useAuthStore.getState();

    if (state.isAuthenticated && state.shouldRefreshToken()) {
      try {
        const { apiService } = await import('../services/api');
        await apiService.refreshTokenSafely();
      } catch (error) {
        console.error('Token refresh failed:', error);
        // If refresh fails, logout user
        await state.logout();
      }
    }
  }, 60000); // Check every minute
};

export const stopTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

// Initialize auth state on app start
export const initializeAuth = async () => {
  const state = useAuthStore.getState();

  // Önce localStorage'dan token ve refresh token'ı direkt oku
  // Çünkü isAuthenticated() çağrısı token expire olunca auth data'yı temizleyebilir
  const token = authService.getToken();
  const refreshToken = authService.getRefreshToken();
  // Token expiry'yi direkt localStorage'dan oku (authService'in private key'ini kullan)
  const expiry = localStorage.getItem('token_expiry');

  // Token ve refresh token varsa
  if (token && refreshToken) {
    // Token expire olmuş mu kontrol et
    const isExpired = expiry ? Date.now() >= parseInt(expiry) : true;

    if (isExpired) {
      // Token expire olmuşsa refresh etmeyi dene
      try {
        await authService.refreshToken();
        // Refresh başarılı oldu, şimdi kullanıcı bilgilerini yükle
        // Zustand persist middleware auth-store'dan user'ı otomatik restore eder
        const persistedUser = useAuthStore.getState().user;
        if (persistedUser) {
          // User zaten Zustand state'inde, sadece token refresh yapıldı
          startTokenRefresh();
        } else {
          // User data yoksa backend'den al
          await state.refreshUser();
          if (useAuthStore.getState().isAuthenticated) {
            startTokenRefresh();
          }
        }
        useAuthStore.setState({ isInitialized: true });
        return;
      } catch (error) {
        console.error('Token refresh failed on initialization:', error);
        // Refresh başarısız olursa auth data'yı temizle
        await state.logout();
        useAuthStore.setState({ isInitialized: true });
        return;
      }
    }

    // Token geçerliyse, kullanıcı bilgilerini yükle
    // Zustand persist middleware auth-store'dan user'ı otomatik restore eder
    const persistedUser = useAuthStore.getState().user;
    if (persistedUser) {
      // User zaten Zustand state'inde
      startTokenRefresh();

      // Subscription bilgisini yükle
      try {
        const { getDefaultBusinessId } = await import('../utils/menuHelper');
        const businessId = await getDefaultBusinessId();
        if (businessId) {
          await state.loadSubscription(businessId);
        }
      } catch (error) {
        console.warn('Failed to load subscription on init:', error);
      }
    } else {
      // User data yoksa, backend'den al
      try {
        await state.refreshUser();
        if (state.isAuthenticated) {
          startTokenRefresh();

          // Subscription bilgisini yükle
          try {
            const { getDefaultBusinessId } = await import('../utils/menuHelper');
            const businessId = await getDefaultBusinessId();
            if (businessId) {
              await state.loadSubscription(businessId);
            }
          } catch (error) {
            console.warn('Failed to load subscription on refresh:', error);
          }
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        // Eğer refreshUser başarısız olursa, logout yap
        await state.logout();
      }
    }
  } else if (token && !refreshToken) {
    // Token var ama refresh token yoksa - bu durumda token expire olmuş olabilir
    // Eğer token expire olmuşsa logout yap
    if (expiry && Date.now() >= parseInt(expiry)) {
      await state.logout();
    } else {
      // Token geçerliyse kullanıcı bilgilerini yükle
      // Zustand persist middleware auth-store'dan user'ı otomatik restore eder
      const persistedUser = useAuthStore.getState().user;
      if (persistedUser) {
        // User zaten Zustand state'inde, ekstra işlem gerekmiyor
      } else {
        try {
          await state.refreshUser();
        } catch (error) {
          await state.logout();
        }
      }
    }
  }

  useAuthStore.setState({ isInitialized: true });
};

